import React, { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Copy, ExternalLink, Eye, Radio, Send } from "lucide-react";
import { timeAgo } from "../shared";

/* ----------------------- Links view (founder shortcuts) ----------------------- */

const PROD_BASE = "https://memorylanecare.net";
const PREVIEW_BASE = "https://memory-support-guide.preview.emergentagent.com";
const PROLIFIC_CODE = "C1PAR2IC"; // "Memory Loss and Dementia Care Navigation" study
const PROLIFIC_COMPLETION_URL = `https://app.prolific.com/submissions/complete?cc=${PROLIFIC_CODE}`;
const PROLIFIC_SCREENOUT_CODE = "C1HDHBIO";
const PROLIFIC_SCREENOUT_URL = `https://app.prolific.com/submissions/complete?cc=${PROLIFIC_SCREENOUT_CODE}`;

const LINK_GROUPS = [
    {
        title: "Best converters — share these first",
        sub: "Ranked by actual GA4 conversions. Each link is UTM-tagged, so GA4 shows you exactly which shared link drove each signup (Reports → Acquisition → Traffic acquisition → session campaign).",
        items: [
            { label: "Homepage", path: "/?utm_source=share&utm_medium=referral&utm_campaign=homepage", note: "Your #1 converter — 61 conversions in the last 28 days." },
            { label: "Memory screener", path: "/tools/memory-check?utm_source=share&utm_medium=referral&utm_campaign=screener", note: "10 warning-signs check — best for worried families. No signup needed." },
            { label: "Instant free signup", path: "/beta?utm_source=share&utm_medium=referral&utm_campaign=beta_signup", note: "Shortest path to an account — email + name, no password, no credit card." },
            { label: "GUIDE eligibility check", path: "/tools/guide-check?utm_source=share&utm_medium=referral&utm_campaign=guide_check", note: "GUIDE completion is a tracked conversion event. Great for Medicare families." },
            { label: "Care journey quiz", path: "/tools/journey-quiz?utm_source=share&utm_medium=referral&utm_campaign=journey_quiz", note: "Interactive — good for social posts." },
            { label: "Article: Paranoia & accusations", path: "/behavior/paranoia-and-accusations?utm_source=share&utm_medium=referral&utm_campaign=article_paranoia", note: "Your most-viewed crisis article — highest-intent readers." },
            { label: "Article: Exit-seeking / wandering", path: "/behavior/exit-seeking?utm_source=share&utm_medium=referral&utm_campaign=article_exit_seeking", note: "Second most-viewed behavior article." },
            { label: "Article: Bathing tips", path: "/resources/bathing-tips-dementia?utm_source=share&utm_medium=referral&utm_campaign=article_bathing", note: "Top how-to article — good for support-group shares." },
        ],
    },
    {
        title: "Public marketing site",
        sub: "What new visitors land on. Share these on socials, in cold emails, or in your bio.",
        items: [
            { label: "Landing page", path: "/", note: "Homepage" },
            { label: "Try Memory Lane (instant signup, no password)", path: "/beta", note: "Email + name → instant free access (beta). Survey is voluntary — no lockout." },
            { label: "Log back in (returning trial users)", path: "/login", note: "Existing users can re-enter with just their email — no password needed." },
            { label: "Public resources (all 50 states)", path: "/resources" },
            { label: "Demo (no-login walkthrough)", path: "/demo" },
            { label: "Terms of Service", path: "/terms", note: "Includes HIPAA disclosure." },
            { label: "Privacy Policy", path: "/privacy" },
        ],
    },
    {
        title: "Research / tester URLs",
        sub: "For Prolific, Reddit outreach, or one-off testers. Send these to caregivers running a paid study.",
        items: [
            { label: "Generic tester link (works for any iid)", path: "/tester/?iid=REPLACE_ME", note: "Replace REPLACE_ME with a unique id. One-off iids skip the screener (you've vetted them); Prolific links use the URL builder below." },
            { label: "Post-study thank-you page", path: "/study-complete", note: "Where testers land after submitting the survey." },
            { label: "Feedback survey (direct)", path: "/feedback", note: "Where the 60-min tester lockout AND the 3-section trial wall send people." },
        ],
    },
    {
        title: "Care navigator + caregiver requests",
        sub: "Lead-gen surfaces — anyone can fill these out, no login required.",
        items: [
            { label: "Care navigator waitlist", path: "/navigator" },
            { label: "Private caregivers + volunteers request", path: "/care-network" },
        ],
    },
];

function copyText(text, successMsg = "Copied.") {
    try {
        navigator.clipboard.writeText(text);
        toast.success(successMsg);
    } catch {
        toast.error("Couldn't copy. Highlight and copy manually.");
    }
}

/* Link performance — visits + signups per utm_campaign, from our own DB
   (no GA4 login needed). Populated by /api/utm/visit pings on landing +
   utm_campaign stored at signup. */
function LinkPerformanceCard() {
    const [rows, setRows] = useState(null);
    useEffect(() => {
        api.get("/admin/utm-performance")
            .then(r => setRows(r.data.rows || []))
            .catch(() => setRows([]));
    }, []);
    return (
        <section className="ml-card p-5 md:p-6" data-testid="link-performance-card">
            <p className="text-xs uppercase tracking-wider font-semibold text-teal-dark">Link performance</p>
            <h2 className="mt-0.5 text-xl font-heading font-medium text-[#1F2624]">Which shared link is winning?</h2>
            <p className="text-sm text-[#4C5551] mt-1">
                Counted from your own database whenever someone lands via a UTM-tagged link below and when they sign up.
            </p>
            {rows === null && <p className="text-sm text-[#6E7A74] mt-4">Loading…</p>}
            {rows !== null && rows.length === 0 && (
                <p className="text-sm text-[#6E7A74] mt-4" data-testid="link-performance-empty">
                    No tracked visits yet — share any link from the &ldquo;Best converters&rdquo; group and this table fills in automatically.
                </p>
            )}
            {rows !== null && rows.length > 0 && (
                <div className="mt-4 overflow-x-auto">
                    <table className="w-full text-sm" data-testid="link-performance-table">
                        <thead>
                            <tr className="text-left text-xs uppercase tracking-wider text-[#6E7A74] border-b border-cream-dark">
                                <th className="py-2 pr-4">Campaign</th>
                                <th className="py-2 pr-4">Visits (7d)</th>
                                <th className="py-2 pr-4">Visits (all)</th>
                                <th className="py-2">Signups</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map(r => (
                                <tr key={r.campaign} className="border-b border-cream-light" data-testid={`link-perf-row-${r.campaign}`}>
                                    <td className="py-2 pr-4 font-mono text-xs text-[#1F2624]">{r.campaign}</td>
                                    <td className="py-2 pr-4 tabular-nums">{r.visits_7d}</td>
                                    <td className="py-2 pr-4 tabular-nums text-[#6E7A74]">{r.visits_total}</td>
                                    <td className="py-2 tabular-nums font-semibold text-teal-dark">{r.signups}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </section>
    );
}

function LinkRow({ label, url, note, testid }) {
    return (
        <li
            data-testid={testid}
            className="rounded-2xl border border-[#E2E8F0] bg-white px-3.5 py-2.5 flex flex-wrap items-center justify-between gap-2"
        >
            <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-[#1F2624] truncate">{label}</p>
                <p className="text-xs text-[#4C5551] font-mono break-all mt-0.5">{url}</p>
                {note && <p className="text-xs text-[#6E7A74] mt-1 leading-snug">{note}</p>}
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
                <button
                    type="button"
                    onClick={() => copyText(url, "Link copied.")}
                    data-testid={`${testid}-copy`}
                    className="inline-flex items-center gap-1 text-xs font-medium px-3 py-1.5 rounded-2xl bg-cream-dark hover:bg-cream-dark/70 text-[#1F2624]"
                >
                    <Copy size={12} strokeWidth={2} /> Copy
                </button>
                <a
                    href={url}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`${testid}-open`}
                    className="inline-flex items-center gap-1 text-xs font-medium px-3 py-1.5 rounded-2xl bg-teal-soft hover:bg-teal/20 text-teal-dark"
                >
                    <ExternalLink size={12} strokeWidth={2} /> Open
                </a>
            </div>
        </li>
    );
}

export default function LinksView() {
    const [base, setBase] = useState(PROD_BASE);
    const [iidDraft, setIidDraft] = useState("reddit-1");
    const [testers, setTesters] = useState([]);
    const [loadingTesters, setLoadingTesters] = useState(true);

    useEffect(() => {
        let active = true;
        (async () => {
            try {
                const r = await api.get("/admin/testers");
                if (active) setTesters(r.data.items || []);
            } catch {
                if (active) toast.error("Couldn't load tester codes.");
            } finally {
                if (active) setLoadingTesters(false);
            }
        })();
        return () => { active = false; };
    }, []);

    const safeIid = (iidDraft || "").trim() || "REPLACE_ME";
    const previewBase = PREVIEW_BASE;

    const buildUrl = (path) => `${base}${path}`;
    const customTester = `${base}/tester/?iid=${encodeURIComponent(safeIid)}`;

    return (
        <div className="space-y-6" data-testid="links-view">
            <header>
                <h1 className="text-3xl md:text-4xl font-heading font-semibold tracking-tight">All your links</h1>
                <p className="mt-2 text-[#4C5551]">
                    Every Memory Lane URL in one place — copy a tester link, open the public site, or grab the survey URL.
                </p>
            </header>

            {/* Base URL switcher — useful when you're testing in preview vs sharing prod links. */}
            <section
                className="ml-card p-5 bg-cream-dark/30 !border-cream-dark"
                data-testid="links-base-switcher"
            >
                <p className="text-xs uppercase tracking-wider font-semibold text-teal mb-2">Build links against</p>
                <div className="flex flex-wrap gap-2">
                    <button
                        type="button"
                        onClick={() => setBase(PROD_BASE)}
                        data-testid="links-base-prod"
                        className={`text-sm font-medium px-4 py-2 rounded-2xl border transition-colors ${base === PROD_BASE
                            ? "bg-teal text-white border-teal"
                            : "bg-white text-[#4C5551] border-[#E2E8F0] hover:bg-cream-dark"
                            }`}
                    >
                        Production · memorylanecare.net
                    </button>
                    <button
                        type="button"
                        onClick={() => setBase(previewBase)}
                        data-testid="links-base-preview"
                        className={`text-sm font-medium px-4 py-2 rounded-2xl border transition-colors ${base === previewBase
                            ? "bg-teal text-white border-teal"
                            : "bg-white text-[#4C5551] border-[#E2E8F0] hover:bg-cream-dark"
                            }`}
                    >
                        Preview · {previewBase.replace("https://", "")}
                    </button>
                </div>
                <p className="mt-3 text-xs text-[#6E7A74]">
                    Production links go to your live site (memorylanecare.net). Preview links hit the dev environment — handy when you&apos;re
                    testing a change before you click Publish.
                </p>
            </section>

            {/* Custom tester URL builder — the one the founder will use most. */}
            <section className="ml-card p-5 !border-stage_late-light bg-stage_late-light/40" data-testid="links-tester-builder">
                <div className="flex items-start gap-3 mb-3">
                    <span className="w-9 h-9 rounded-2xl bg-stage_late-dark text-white flex items-center justify-center shrink-0">
                        <Radio strokeWidth={1.8} size={16} />
                    </span>
                    <div className="flex-1">
                        <h2 className="font-heading font-medium text-lg text-[#1F2624]">Build a one-off tester link</h2>
                        <p className="text-sm text-[#4C5551] mt-0.5">
                            Pick any short id (their initials, the source, a date — anything unique) and copy. The link
                            auto-provisions a clean tester account on first visit and starts the 30-minute window.
                        </p>
                    </div>
                </div>
                <label className="ml-label" htmlFor="links-iid-input">Tester id (iid)</label>
                <div className="flex flex-wrap items-center gap-2">
                    <input
                        id="links-iid-input"
                        type="text"
                        data-testid="links-iid-input"
                        value={iidDraft}
                        onChange={(e) => setIidDraft(e.target.value)}
                        placeholder="reddit-april-1"
                        className="ml-input flex-1 min-w-[200px]"
                    />
                    <button
                        type="button"
                        onClick={() => copyText(customTester, "Tester link copied.")}
                        data-testid="links-tester-copy"
                        className="inline-flex items-center gap-1.5 bg-stage_late-dark hover:bg-stage_late-dark/90 text-white rounded-2xl px-4 py-2.5 text-sm font-medium"
                    >
                        <Copy size={14} strokeWidth={2} /> Copy link
                    </button>
                    <a
                        href={customTester}
                        target="_blank"
                        rel="noreferrer"
                        data-testid="links-tester-open"
                        className="inline-flex items-center gap-1.5 bg-white border border-stage_late-dark text-stage_late-dark rounded-2xl px-4 py-2.5 text-sm font-medium"
                    >
                        <ExternalLink size={14} strokeWidth={2} /> Open
                    </a>
                </div>
                <p
                    className="mt-2 text-xs text-[#4C5551] font-mono break-all"
                    data-testid="links-tester-preview-url"
                >
                    {customTester}
                </p>
            </section>


            {/* Prolific study setup — completion code + study URL. */}
            <section
                className="ml-card p-5 !border-stage_late-light bg-stage_late-light/30"
                data-testid="links-prolific"
            >
                <div className="flex items-start gap-3 mb-4">
                    <span className="w-9 h-9 rounded-2xl bg-stage_late-dark text-white flex items-center justify-center shrink-0">
                        <Eye strokeWidth={1.8} size={16} />
                    </span>
                    <div className="flex-1">
                        <h2 className="font-heading font-medium text-lg text-[#1F2624]">Prolific study</h2>
                        <p className="text-sm text-[#4C5551] mt-0.5">
                            Drop these into Prolific&apos;s study setup. The PROLIFIC_PID Prolific appends becomes
                            the tester&apos;s iid automatically, and the completion code is what they paste back
                            to release payment.
                        </p>
                    </div>
                </div>

                <div className="space-y-3">
                    <div className="bg-white border-2 border-stage_late-dark rounded-2xl px-4 py-3.5 flex flex-wrap items-center justify-between gap-3">
                        <div className="min-w-0">
                            <p className="text-[10px] uppercase tracking-wider font-semibold text-stage_late-dark mb-0.5">
                                Completion code (what testers paste into Prolific)
                            </p>
                            <code
                                data-testid="links-prolific-code"
                                className="font-mono text-xl tracking-widest text-stage_late-dark font-semibold select-all"
                            >
                                {PROLIFIC_CODE}
                            </code>
                        </div>
                        <button
                            type="button"
                            onClick={() => copyText(PROLIFIC_CODE, "Completion code copied.")}
                            data-testid="links-prolific-code-copy"
                            className="inline-flex items-center gap-1.5 bg-stage_late-dark hover:bg-stage_late-dark/90 text-white rounded-2xl px-4 py-2 text-xs font-medium"
                        >
                            <Copy size={12} strokeWidth={2} /> Copy
                        </button>
                    </div>

                    <LinkRow
                        label="Prolific completion URL (set as study completion URL on Prolific)"
                        url={PROLIFIC_COMPLETION_URL}
                        note="In Prolific's study settings → Completion URL field."
                        testid="links-prolific-completion-url"
                    />

                    <LinkRow
                        label="Prolific screen-out URL (for participants who don't qualify)"
                        url={PROLIFIC_SCREENOUT_URL}
                        note={`Code ${PROLIFIC_SCREENOUT_CODE}. The /screener page sends failed participants here automatically.`}
                        testid="links-prolific-screenout-url"
                    />

                    <LinkRow
                        label="Study URL to give Prolific"
                        url={`${base}/tester/?PROLIFIC_PID={{%PROLIFIC_PID%}}&STUDY_ID={{%STUDY_ID%}}&SESSION_ID={{%SESSION_ID%}}`}
                        note="Paste this into Prolific's Study URL field — they'll replace {{%PROLIFIC_PID%}} per participant. We capture it as the iid automatically."
                        testid="links-prolific-study-url"
                    />
                </div>
            </section>


            {/* Link performance — visits + signups per shared campaign */}
            <LinkPerformanceCard />

            {/* Static link groups */}
            {LINK_GROUPS.map((group) => (
                <section
                    key={group.title}
                    className="ml-card p-5"
                    data-testid={`links-group-${group.title.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                >
                    <div className="mb-3">
                        <h2 className="font-heading font-medium text-base">{group.title}</h2>
                        {group.sub && <p className="text-xs text-[#4C5551] mt-0.5">{group.sub}</p>}
                    </div>
                    <ul className="space-y-2">
                        {group.items.map((it) => (
                            <LinkRow
                                key={it.path}
                                label={it.label}
                                note={it.note}
                                url={buildUrl(it.path)}
                                testid={`link-${it.path.replace(/[^a-z0-9]+/gi, "-").toLowerCase().replace(/^-|-$/g, "") || "root"}`}
                            />
                        ))}
                    </ul>
                </section>
            ))}

            {/* Admin-issued tester codes — pulled live from /admin/testers */}
            <section className="ml-card p-5" data-testid="links-group-issued-testers">
                <div className="flex flex-wrap items-end justify-between gap-2 mb-3">
                    <div>
                        <h2 className="font-heading font-medium text-base">Admin-issued tester codes</h2>
                        <p className="text-xs text-[#4C5551] mt-0.5">
                            Pre-generated personal codes from the Applications tab (&quot;Generate tester links&quot;). Each one
                            is a unique, single-use link.
                        </p>
                    </div>
                    <span className="text-xs text-[#4C5551] tabular-nums">
                        {loadingTesters ? "Loading…" : `${testers.length} total`}
                    </span>
                </div>
                {!loadingTesters && testers.length === 0 && (
                    <p
                        data-testid="links-issued-empty"
                        className="text-sm text-[#4C5551] italic bg-cream-dark/30 rounded-2xl px-4 py-3"
                    >
                        None yet. Go to <strong>Applications → Generate tester links</strong> to mint a batch.
                    </p>
                )}
                {testers.length > 0 && (
                    <ul className="space-y-2">
                        {testers.map((t) => {
                            const code = (t.tester_code || "").trim();
                            const url = `${base}/tester/${encodeURIComponent(code)}`;
                            const used = !!t.tester_used_at;
                            return (
                                <li
                                    key={t.id}
                                    data-testid={`link-tester-code-${code}`}
                                    className={`rounded-2xl border px-3.5 py-2.5 flex flex-wrap items-center justify-between gap-2 ${used
                                        ? "bg-cream-dark/40 border-cream-dark"
                                        : "bg-white border-[#E2E8F0]"
                                        }`}
                                >
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <p className="text-sm font-medium text-[#1F2624]">{code}</p>
                                            {t.tester_label && (
                                                <span className="text-[10px] uppercase tracking-wider font-semibold bg-stage_late-light text-stage_late-dark px-2 py-0.5 rounded-2xl">
                                                    {t.tester_label}
                                                </span>
                                            )}
                                            {used && (
                                                <span className="text-[10px] uppercase tracking-wider font-semibold bg-stage_early/40 text-stage_early-dark px-2 py-0.5 rounded-2xl">
                                                    Used
                                                </span>
                                            )}
                                        </div>
                                        <p className="text-xs text-[#4C5551] font-mono break-all mt-0.5">{url}</p>
                                        <p className="text-[11px] text-[#6E7A74] mt-1">
                                            {t.login_count || 0}× logins
                                            {t.last_login_at ? ` · last ${timeAgo(t.last_login_at)}` : " · never used"}
                                        </p>
                                    </div>
                                    <div className="flex items-center gap-1.5 shrink-0">
                                        <button
                                            type="button"
                                            onClick={() => copyText(url, `${code} copied.`)}
                                            data-testid={`link-tester-code-${code}-copy`}
                                            className="inline-flex items-center gap-1 text-xs font-medium px-3 py-1.5 rounded-2xl bg-cream-dark hover:bg-cream-dark/70 text-[#1F2624]"
                                        >
                                            <Copy size={12} strokeWidth={2} /> Copy
                                        </button>
                                        <a
                                            href={url}
                                            target="_blank"
                                            rel="noreferrer"
                                            data-testid={`link-tester-code-${code}-open`}
                                            className="inline-flex items-center gap-1 text-xs font-medium px-3 py-1.5 rounded-2xl bg-teal-soft hover:bg-teal/20 text-teal-dark"
                                        >
                                            <ExternalLink size={12} strokeWidth={2} /> Open
                                        </a>
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                )}
            </section>
        </div>
    );
}
